import { computed, type MaybeRefOrGetter, toValue } from 'vue';

import { useAccountInfoQuery } from '@/platform/identity/queries';
import { useUserStore } from '@/platform/identity/store';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';

import {
  clusterMetaApi,
  type CheckCredentialRequest,
  type CheckCredentialResponse,
  type CheckOrgFreeTierResponse,
  type UpsertResponse,
  type LegacyClusterRecordResponse,
  type CspSummary,
  type ExternalIdResponse,
  type RegionSummary,
} from './api';
import { clusterPriceSchema, freeTierListSchema } from './schemas/step1-responses';
import { freeTierOffers, type FreeTierOffer } from './step1/free-tier';

import type { ClusterInfo } from './cluster-state';
import type { DeployVmListResponse } from './step1/vm-options';
import type { AzureCredentialBody } from './step2/credential-payload';
import type { VersionThresholds } from './version-tag';

export const CSP_LIST_KEY = ['csp-list'] as const;
export const REGION_LIST_KEY = ['region-list'] as const;
// Invalidating this suffix-less base hits every account's list: vue-query matches by prefix.
export const CLUSTER_LIST_KEY = ['cluster', 'cluster-list'] as const;

// Account-scoped per the query-key convention: accountID is the cache
// partition for account-owned data. Logout / timeout / SSO re-auth reload
// the document; plain password login happens before these queries exist, so
// the suffix still acts as a stable partition and
// invalidateQueries({ queryKey: CSP_LIST_KEY }) matches every account
// suffix because vue-query does prefix matching.
// Host-owned CSP list. The sub-side store sentinel fires
// SUB_EVENTS.CONTEXT_INVALIDATED whenever sub mutates its own cluster.cspList;
// the host listener in sub-broadcaster.ts then invalidates this query.
//
// staleTime 0: an operator toggling a CSP's or region's availability in the
// admin console has no path into this cache, and the sidebar's observer lives
// as long as the app — so a cached window meant the menu kept offering a
// provider that had been switched off. Being always stale, any newly mounted
// observer (entering /cloud/*, opening a network dialog) refetches and the
// sidebar picks the answer up through the shared key.
export function useCspListQuery() {
  const { info } = storeToRefs(useUserStore());
  return useQuery<CspSummary[]>({
    queryKey: computed(() => [...CSP_LIST_KEY, info.value?.accountID ?? null] as const),
    queryFn: async () => {
      const res = await clusterMetaApi['/csp/list']();
      return res?.list ?? [];
    },
    enabled: computed(() => !!info.value),
    staleTime: 0,
  });
}

// Account-scoped cluster inventory. `enabled` gates the fetch — Support's
// ticket dialog only fetches when the user has cluster@view and the dialog is
// open, mirroring legacy (no cluster-list call otherwise).
//
// staleTime 0: cluster inventory is volatile — clusters are created and torn
// down while a session is open, so a picker must reflect current inventory on
// each open. With staleTime 0 the query is always stale, so re-enabling it (reopening
// the dialog) refetches instead of serving a cached list that would drop a
// just-created cluster. Legacy fetched cluster-list on every dialog open.
export function useClusterListQuery(enabled: MaybeRefOrGetter<boolean>) {
  const { info } = storeToRefs(useUserStore());
  return useQuery<ClusterInfo[]>({
    queryKey: computed(() => [...CLUSTER_LIST_KEY, info.value?.accountID ?? null] as const),
    queryFn: async () => {
      const res = await clusterMetaApi['/cluster/cluster-list']();
      return res?.list ?? [];
    },
    enabled: computed(() => toValue(enabled) && !!info.value),
    staleTime: 0,
  });
}

// `available: true` is the only availability filter in play: /csp/list takes
// none, so a CSP switched off in admin still comes back and the sidebar's
// cspList ∩ region-cspId gate drops it only once its last region goes away.
// staleTime 0 for the same reason as the CSP list above.
export function useRegionListQuery() {
  const { info } = storeToRefs(useUserStore());
  return useQuery<RegionSummary[]>({
    queryKey: computed(() => [...REGION_LIST_KEY, info.value?.accountID ?? null] as const),
    queryFn: async () => {
      const res = await clusterMetaApi['/cluster/region-list']({
        filters: [{ available: true }],
      });
      return res?.list ?? [];
    },
    enabled: computed(() => !!info.value),
    staleTime: 0,
  });
}

// One-shot External ID for an IAM role trust policy. A fresh eid each call, so
// it's a mutation fetched on demand (audit-log-export create dialog open).
// Best-effort lookups: a failure degrades to "no version chip", "no free tier",
// "classic stays gated", so no error toast.
const SILENT = { ignoreErrorMsg: true } as const;

export const SR_RELEASE_THRESHOLDS_KEY = ['sr-release-ga-thresholds'] as const;
const ORG_FREE_TIER_KEY = ['cluster', 'check-org-free-tier'] as const;
const LEGACY_CLUSTER_RECORD_KEY = ['cluster', 'legacy-cluster-record'] as const;

export function useSrReleaseThresholdsQuery() {
  return useQuery<VersionThresholds>({
    queryKey: SR_RELEASE_THRESHOLDS_KEY,
    queryFn: async () => {
      const res = await clusterMetaApi['/sr-release-ga-threshold/list'](undefined, SILENT);
      const out: VersionThresholds = {};
      for (const entry of res?.list ?? []) {
        if (entry.minor_version) {
          out[entry.minor_version] = {
            min_ga: entry.min_ga_version ?? '',
            min_stable: entry.min_stable_version ?? '',
          };
        }
      }
      return out;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// `false` disables every create affordance, so a failed fetch must not read as
// "no": callers treat `undefined` as permitted.
export function useOrgFreeTierQuery() {
  const { info } = storeToRefs(useUserStore());
  const accountInfo = useAccountInfoQuery();
  const orgId = computed(() => accountInfo.data.value?.orgId ?? '');
  return useQuery<CheckOrgFreeTierResponse>({
    queryKey: computed(
      () => [...ORG_FREE_TIER_KEY, info.value?.accountID ?? null, orgId.value] as const,
    ),
    queryFn: async () =>
      (await clusterMetaApi['/cluster/check-org-free-tier']({ orgId: orgId.value }, SILENT)) ?? {},
    enabled: computed(() => !!info.value && !!orgId.value),
  });
}

// Empty is the common case and means "classic is gated", not an error.
export function useLegacyClusterRecordQuery() {
  const { info } = storeToRefs(useUserStore());
  return useQuery<number[]>({
    queryKey: computed(
      () => [...LEGACY_CLUSTER_RECORD_KEY, info.value?.accountID ?? null] as const,
    ),
    queryFn: async () => {
      const res: LegacyClusterRecordResponse =
        (await clusterMetaApi['/account/legacy-cluster/account-record'](undefined, SILENT)) ?? {};
      return res.resource_struct_list ?? [];
    },
    enabled: computed(() => !!info.value),
  });
}

export function useGetExternalId() {
  return useMutation<ExternalIdResponse, unknown, void>({
    mutationFn: () => clusterMetaApi['/cluster/get-external-id'](),
  });
}

// A 2xx only means the check ran: `check_err_code === 20000` is the only pass,
// other non-zero codes are refusals, and 0 / absent means it did not run.
export function useCheckCredential() {
  return useMutation<CheckCredentialResponse, unknown, CheckCredentialRequest>({
    mutationFn: (body) => clusterMetaApi['/cluster/check-credential'](body),
  });
}

export const CREDENTIAL_CHECK_PASSED = 20000;

// A 2xx with no `cid` is a refusal: callers gate on the returned id, not on the promise resolving.
export function useCreateCredential() {
  return useMutation<UpsertResponse, unknown, { credential: unknown }>({
    mutationFn: (body) => clusterMetaApi['/cluster/create-credential'](body),
  });
}

// Same refusal contract: a 2xx with no `cid`.
export function useCreateAkSkCredential() {
  return useMutation<UpsertResponse, unknown, AzureCredentialBody>({
    mutationFn: (body) => clusterMetaApi['/cluster/create-ak-sk-credential'](body),
  });
}

export function useCreateStorageConf() {
  return useMutation<UpsertResponse, unknown, { storage_conf: unknown; credential_id?: string }>({
    mutationFn: (body) => clusterMetaApi['/cluster/create-storage-conf'](body),
  });
}

const AVAILABLE_ONLY = 2;

export interface FreeTierParams {
  regionId: string;
  cspId: string;
}

// gcTime 0: a cached list is served synchronously on a second visit, so the
// watchers that seed region / version / arch from its arrival never fire.
// placeholderData undefined opts out of the client-wide keepPreviousData, or the
// previous region's node types stay pickable until the new list lands.
const WIZARD_CATALOG = { staleTime: 0, gcTime: 0, placeholderData: undefined } as const;

export const CREATE_REGION_LIST_KEY = ['cluster', 'region-list', 'create'] as const;

export function useCreateRegionListQuery(cspId: MaybeRefOrGetter<string | undefined>) {
  return useQuery<RegionSummary[]>({
    queryKey: computed(() => [...CREATE_REGION_LIST_KEY, toValue(cspId) ?? null] as const),
    queryFn: async () => {
      const res = await clusterMetaApi['/cluster/region-list']({
        common: { orders: [{ col: 'region', desc: false }] },
        filters: [{ available: true, cspId: toValue(cspId) ?? '' }],
      });
      return res?.list ?? [];
    },
    enabled: computed(() => !!toValue(cspId)),
    ...WIZARD_CATALOG,
  });
}

export const ARCH_LIST_KEY = ['cluster', 'arch-list'] as const;
export const VM_LIST_KEY = ['cluster', 'deploy-vm-list'] as const;
export const FREE_TIER_KEY = ['cluster', 'list-free-tier'] as const;
export const HOUR_PRICE_KEY = ['cluster', 'hour-price'] as const;

export interface ArchOption {
  value: string;
  label: string;
}

export function useArchListQuery(regionId: MaybeRefOrGetter<string | undefined>) {
  return useQuery<ArchOption[]>({
    queryKey: computed(() => [...ARCH_LIST_KEY, toValue(regionId) ?? null] as const),
    queryFn: async () => {
      const res = await clusterMetaApi['/cluster/arch-list']({ regionId: toValue(regionId) ?? '' });
      return (res?.list ?? [])
        .filter((entry) => entry.arch)
        .map((entry) => ({
          value: entry.arch as string,
          label: entry.display_arch || (entry.arch as string),
        }));
    },
    enabled: computed(() => !!toValue(regionId)),
    ...WIZARD_CATALOG,
  });
}

export interface VmListParams {
  clusterType: number;
  regionId: string;
  cspId: string;
  arch: string;
}

export function useDeployVmListQuery(params: MaybeRefOrGetter<VmListParams | null>) {
  return useQuery<DeployVmListResponse>({
    queryKey: computed(() => [...VM_LIST_KEY, toValue(params)] as const),
    queryFn: () => {
      const current = toValue(params);
      if (!current) return Promise.resolve({});
      return clusterMetaApi['/cluster/deploy-VM-list']({
        clusterType: current.clusterType,
        vm: {
          common: {
            limit: 99_999,
            orders: [
              { col: 'instance_type', desc: false },
              { col: 'v_cpu', desc: false },
              { col: 'vm_cate', desc: false },
            ],
          },
          arch: current.arch,
          regionId: current.regionId,
          cspId: current.cspId,
          available: AVAILABLE_ONLY,
        },
        // An envelope with no `data` unwraps to undefined, which vue-query turns into a query error.
      }).then((res) => res ?? {});
    },
    enabled: computed(() => !!toValue(params)),
    ...WIZARD_CATALOG,
  });
}

export function useFreeTierListQuery(params: MaybeRefOrGetter<FreeTierParams | null>) {
  return useQuery<FreeTierOffer[]>({
    queryKey: computed(() => [...FREE_TIER_KEY, toValue(params)] as const),
    queryFn: async () => {
      const current = toValue(params);
      if (!current) return [];
      const res = await clusterMetaApi['/cluster/list-free-tier'](current, {
        schema: freeTierListSchema,
      });
      return freeTierOffers(res);
    },
    enabled: computed(() => !!toValue(params)),
    ...WIZARD_CATALOG,
  });
}

/**
 * The hourly quote for one configuration. `null` while the configuration is
 * incomplete, which is also what keeps the request from being sent.
 */
export function usePriceQuery(units: MaybeRefOrGetter<unknown>) {
  return useQuery({
    queryKey: computed(() => [...HOUR_PRICE_KEY, toValue(units)] as const),
    queryFn: async () => {
      const res = await clusterMetaApi['/cluster/hour-price'](
        { price: { packageUnits: toValue(units) } },
        { schema: clusterPriceSchema },
      );
      return res.price ?? {};
    },
    enabled: computed(() => !!toValue(units)),
  });
}

/**
 * Whether a cluster of this name already exists. Rejects rather than answering
 * `false` when the check itself fails, so the name validator can fail closed.
 */
export async function isClusterNameTaken(name: string): Promise<boolean> {
  const res = await clusterMetaApi['/cluster/check-cluster-name']({ cluster_name: name });
  return !!res?.exist;
}
